<script lang="ts">
  import MultiSearchAndSelectDropdown from "@shared/svelte/Components/MultiSearchAndSelectDropdown.svelte";
  import DatePicker from "@shared/svelte/Components/DatePicker.svelte";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import type { Distributor } from "@shared/types/Distributor";
  import Arrow from "@shared/svelte/Components/Arrow.svelte";
  import * as _ from "lodash";

  export let stateParams: any;
  export let distributors: Distributor[];
  export let refreshState: any;
  export let orderStates: any;

  let selectedDistributors = [];
  let selectedOrderStates = [];
  let distributorSearchValue = "";

  let allDistributors = _.clone(distributors);

  let showFilters: boolean = false;
  let search: string = stateParams.search || "";

  let dateFilter = {
    fromDate: stateParams.fromDate,
    toDate: stateParams.toDate,
    displayFromDate: null,
    displayToDate: null,
  };
  if (search || dateFilter.fromDate) {
    showFilters = true;
  }

  const updateSearch = function () {
    var attrs = { search: search };
    if (!search || search.length == 0) {
      attrs.search = null;
    }
    refreshState(attrs);
  };

  const handleSearchKeypress = function (event) {
    if (event.which == 13) {
      updateSearch();
    }
  };

  const handleDateChange = function () {
    refreshState({ fromDate: dateFilter.fromDate, toDate: dateFilter.toDate });
  };

  const setupFiltersFromParams = function () {
    if (stateParams.distributors) setupDistributorFilter();
    if (stateParams.orderStates) setupOrderStateFilter();
  };

  const setupDistributorFilter = function () {
    selectedDistributors = [];
    stateParams.distributors.forEach(function (distributor_id) {
      var distributor = _.find(distributors, { id: parseInt(distributor_id) });
      if (distributor) {
        selectedDistributors.push(distributor.id);
        showFilters = true;
      }
    });
  };

  const handleDistributorSearch = e => {
    let value = e.target.value?.toLowerCase();
    if (!value || value == "") {
      distributors = allDistributors;
    } else {
      distributors = distributors.filter(
        ({ name }) => value == name.toLowerCase().substring(0, value.length)
      );
    }
  };

  const handleDistributorSelect = function (e) {
    let selectedDistributorIds: number[];
    if (e.target.checked) {
      selectedDistributorIds = selectedDistributors.concat([Number(e.target.value)]);
    } else {
      selectedDistributorIds = selectedDistributors.filter(x => x != Number(e.target.value));
    }
    refreshState({ distributors: selectedDistributorIds });
  };

  const handleDistributorFilterClear = function (e) {
    selectedDistributors = [];
    refreshState({ distributors: [] });
  };

  const setupOrderStateFilter = function () {
    stateParams.orderStates.forEach(function (state) {
      var orderState = _.find(orderStates, { state: state });
      if (orderState) {
        selectedOrderStates.push(orderState.id);
        showFilters = true;
      }
    });
  };

  const handleOrderStateSelect = function (e) {
    let selectedOrderStateIds: string[];
    if (e.target.checked) {
      selectedOrderStateIds = selectedOrderStates.concat([e.target.value]);
    } else {
      selectedOrderStateIds = selectedOrderStates.filter(x => x != e.target.value);
    }
    refreshState({ orderStates: selectedOrderStateIds });
  };

  const handleOrderStateClear = function (e) {
    selectedOrderStates = [];
    refreshState({ orderStates: [] });
  };

  setupFiltersFromParams();

</script>

<!-- search -->
<div
  class="background-white padding--smaller padding--small--sm margin-left--none margin-right--none fizz-stack-16"
  class:grid-row={$LayoutStore.isDesktop}
  class:fizz-flex-row={$LayoutStore.isMobile}
  class:fizz-flex-wrap={$LayoutStore.isMobile}
>
  <div
    class="grid-col c12-12"
    class:c10-12--sm={$LayoutStore.isDesktop}
    style={$LayoutStore.isMobile ? "margin-right: 0" : ""}
  >
    <div class="input-wrapper full-width vertical-align-middle">
      <input
        type="text"
        bind:value={search}
        on:change={updateSearch}
        on:keypress={handleSearchKeypress}
        data-testid="search"
        class="input"
        placeholder="Search by product name or order ID"
      />
    </div>
  </div>
  <div class="grid-col c12-12 align-right" class:c2-12--sm={$LayoutStore.isDesktop}>
    <button
      id="show-filters-button"
      class="button button--secondary fizz-flex-row-inline fizz-full-width fizz-button-icon"
      style="padding: 0.55rem 1rem 0.55rem;"
      on:click={() => (showFilters = !showFilters)}
    >
      <span style="margin-right: 0.25rem;">Filter</span>
      <Arrow direction={showFilters ? "up" : "down"} />
    </button>
  </div>
</div>
<!-- // search -->

<!-- filters -->
{#if showFilters}
  <div id="orders_filters" class="fizz-flex-row-responsive fizz-surface fizz-inset-squish-24">
    <div id="distributors_filter" class="fizz-flex-item">
      <div class="label padding-bottom--smallest">Distributors</div>
      <div class="grid-row">
        <div class="grid-col c12-c12 fizz-styles">
          <MultiSearchAndSelectDropdown
            minWidth={"20rem"}
            options={distributors}
            defaultButtonLabel="All Distributors"
            object="distributor"
            searchValue={distributorSearchValue}
            onDistributorSearch={handleDistributorSearch}
            onCheckboxChange={handleDistributorSelect}
            onClear={handleDistributorFilterClear}
            bind:selectedValues={selectedDistributors}
            alignmentOptions={{ desktop: { xPosition: "left" }, mobile: { xPosition: "left" } }}
          />
        </div>
      </div>
    </div>
    <div id="order_states_filter" class="fizz-flex-item">
      <div class="label margin-bottom--smallest">Order States</div>
      <div class="grid-row">
        <div class="grid-col c12-c12 fizz-styles">
          <MultiSearchAndSelectDropdown
            minWidth={"20rem"}
            options={orderStates}
            defaultButtonLabel="All Order States"
            object="Order State"
            searchValue={""}
            useSearch={false}
            onDistributorSearch={handleDistributorSearch}
            onCheckboxChange={handleOrderStateSelect}
            onClear={handleOrderStateClear}
            bind:selectedValues={selectedOrderStates}
            alignmentOptions={{ desktop: { xPosition: "left" }, mobile: { xPosition: "left" } }}
          />
        </div>
      </div>
    </div>
    <div id="from_date" class="fizz-text-medium fizz-flex-item">
      <div class="label padding-bottom--smallest">Filter by Dates</div>
      <DatePicker
        bind:startDate={dateFilter.fromDate}
        bind:endDate={dateFilter.toDate}
        defaultButtonLabel={"All Dates"}
        runFilters={() => handleDateChange()}
        maxDate={new Date()}
        selectButtonStyle={true}
        extraLabelClasses={"fizz-text-strong"}
      />
    </div>
  </div>
{/if}
<!-- // filters -->
